import { Alert, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  AutoComplete,
  FormContainer,
  FormInput,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import MainApi from "../../../../services/mainApi.service";
import {
  createQuestion,
  getQuestionById,
  updateQuestionById,
} from "../../../../services/question.service";
import { TQuestionForm, questionSchema } from "./QuestionSchema";
import { useCallback, useEffect, useState } from "react";
import { QuestionOptionType } from "./QuestionOptionType";
import { QuestionAlternatives } from "./QuestionAlternatives";
import { QuestionCategory } from "./QuestionCategory";
import { QuestionDescription } from "./QuestionDescription";
import { QuestionGuidanceTextPosition } from "./QuestionGuidanceTextPosition";
import { QuestionGuidanceText } from "./QuestionGuidanceText";

const QUESTION_EVALUATION_TYPE_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/QuestionEvaluationType/";
const questionEvalutionTypeService = new MainApi<ISelectType[]>(
  QUESTION_EVALUATION_TYPE_ROUTE,
);

const translationPath = "page.assessments.questions.";

const Question = () => {
  const { t } = useTranslation();

  const [evaluationTypes, setEvaluationTypes] = useState<ISelectType[]>();
  const [locked, setLocked] = useState(false);

  const { sendRequest, loading: eLoading } = useFetch(
    questionEvalutionTypeService.list,
  );

  const fetchEvaluationTypes = useCallback(async () => {
    const { data, success } = await sendRequest(null);
    if (data && success) {
      setEvaluationTypes(data);
    }
  }, [sendRequest]);

  useEffect(() => {
    fetchEvaluationTypes();
  }, [fetchEvaluationTypes]);

  const { sendRequest: save, loading: sLoading } = useFetch(createQuestion);

  const { sendRequest: update, loading: uLoading } =
    useFetch(updateQuestionById);

  const { sendRequest: get, loading: gLoading } = useFetch(getQuestionById);

  const saveHandler = (v: TQuestionForm) =>
    save({
      ...v,
      questionCategoryId: v.questionCategory.id,
      justificationIntegrationId: v.justificationIntegrationId,
      questionEvaluationTypeId: v.questionEvaluationType.id,
      guidanceText: v.guidanceText,
      guidanceTextPositionId: v.guidanceTextPosition?.id ?? null,
      questionOptionTypes: v.questionOptionType
        ? {
            ...v.questionOptionType,
            isRequiredJustification: v.questionOptionType
              .isRequiredJustification
              ? v.questionOptionType.isRequiredJustification
              : false,
            alwaysShowJustification: v.questionOptionType
              .alwaysShowJustification
              ? v.questionOptionType.alwaysShowJustification
              : false,
            requiredJustificationLessThan:
              v.questionOptionType.isRequiredJustification &&
              v.questionEvaluationType.code !== "11"
                ? Number(v.questionOptionType.requiredJustificationLessThan)
                : undefined,
            requiredJustificationOptions: v.questionOptionType
              .isRequiredJustification
              ? v.questionOptionType.requiredJustificationOptions?.trim()
              : undefined,
            requiredJustificationMinChars: v.questionOptionType
              .isRequiredJustification
              ? Number(v.questionOptionType.requiredJustificationMinChars)
              : undefined,
            requiredJustificationMessage:
              v.questionOptionType &&
              v.questionOptionType.isRequiredJustification &&
              v.questionOptionType.requiredJustificationMessage
                ? v.questionOptionType.requiredJustificationMessage
                : undefined,
            isRequired: v.questionOptionType.isRequired
              ? v.questionOptionType.isRequired
              : false,
            minChars: Number(v.questionOptionType.minChars),
          }
        : undefined,
    });

  const updateHandler = (v: TQuestionForm, id: string) =>
    update({
      item: {
        ...v,
        id,
        questionEvaluationTypeId: v.questionEvaluationType.id,
        questionCategoryId: v.questionCategory.id,
        justificationIntegrationId: v.justificationIntegrationId,
        guidanceText: v.guidanceText,
        guidanceTextPositionId: v.guidanceTextPosition?.id ?? null,
        questionOptionTypes: v.questionOptionType
          ? {
              ...v.questionOptionType,
              isRequiredJustification: v.questionOptionType
                .isRequiredJustification
                ? v.questionOptionType.isRequiredJustification
                : false,
              alwaysShowJustification: v.questionOptionType
                .alwaysShowJustification
                ? v.questionOptionType.alwaysShowJustification
                : false,
              requiredJustificationLessThan:
                v.questionOptionType.isRequiredJustification &&
                v.questionEvaluationType.code !== "11"
                  ? Number(v.questionOptionType.requiredJustificationLessThan)
                  : undefined,
              requiredJustificationOptions: v.questionOptionType
                .isRequiredJustification
                ? v.questionOptionType.requiredJustificationOptions?.trim()
                : undefined,
              requiredJustificationMinChars: v.questionOptionType
                .isRequiredJustification
                ? Number(v.questionOptionType.requiredJustificationMinChars)
                : undefined,
              requiredJustificationMessage:
                v.questionOptionType &&
                v.questionOptionType.isRequiredJustification &&
                v.questionOptionType.requiredJustificationMessage
                  ? v.questionOptionType.requiredJustificationMessage
                  : undefined,
              isRequired: v.questionOptionType.isRequired
                ? v.questionOptionType.isRequired
                : false,
              minChars: Number(v.questionOptionType.minChars),
            }
          : undefined,
        questionAlternatives: v.questionAlternatives
          ? v.questionAlternatives.map((alt) => ({
              id: alt.id,
              questionEvaluationTypeAlternativeId:
                alt.questionEvaluationTypeAlternativeId,
              nrOption: alt.nrOption,
              displayOrder: alt.displayOrder,
              integrationId: alt.integrationId,
              label: alt.label,
            }))
          : null,
      },
      id: id,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TQuestionForm>> => {
      const { data } = await get(id);
      if (data) {
        setLocked(data.locked);
        return {
          data: {
            ...data,
            id: data.id ?? undefined,
            description: data.description ?? null,
            questionEvaluationType: data.questionEvaluationType,
            justificationIntegrationId: data.justificationIntegrationId,
            guidanceText: data.guidanceText ?? null,
            guidanceTextPosition: data.guidanceTextPosition ?? null,
            questionOptionType: data.questionOptionType
              ? {
                  ...data.questionOptionType,
                  requiredJustificationLessThan: data.questionOptionType
                    .requiredJustificationLessThan
                    ? data.questionOptionType.requiredJustificationLessThan.toString()
                    : "",
                  requiredJustificationMinChars: data.questionOptionType
                    .requiredJustificationMinChars
                    ? data.questionOptionType.requiredJustificationMinChars.toString()
                    : "",
                  alwaysShowJustification:
                    data.questionOptionType.alwaysShowJustification,
                  isRequired: data.questionOptionType.isRequired,
                  minChars: data.questionOptionType.minChars
                    ? data.questionOptionType.minChars.toString()
                    : undefined,
                }
              : undefined,
            questionAlternatives: data.questionAlternatives
              ? data.questionAlternatives.map((alt) => ({
                  id: alt.id ?? undefined,
                  questionEvaluationTypeAlternativeId:
                    alt.questionEvaluationTypeAlternativeId,
                  nrOption: alt.nrOption,
                  displayOrder: alt.displayOrder,
                  integrationId: alt.integrationId,
                  label: alt.label,
                }))
              : [],
          },
          success: true,
        };
      }
      return {
        data: null,
        success: false,
      };
    },
    [get],
  );

  const loading = sLoading || uLoading || gLoading;

  if (eLoading) return null;

  return (
    <FormContainer<TQuestionForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={evaluationTypes ? getHandler : undefined}
      loading={loading}
      title={t(translationPath + "question")}
      schema={questionSchema}
      subtitle={t(translationPath + "new_question")}
      subtitleWatchField={"title"}
      formActionsOnBottom={false}
      disableSave={locked}
      fields={(type) => (
        <Grid container spacing={2}>
          {locked && (
            <Grid item xs={12}>
              <Alert severity="warning">
                {t("page.evaluation.forms.locked_question_warning")}
              </Alert>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            component="fieldset"
            disabled={locked}
            sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <AutoComplete
                  name="questionEvaluationType"
                  label={
                    t(translationPath + "question_evaluation_type") ||
                    "Question Evaluation Type"
                  }
                  size="small"
                  fullWidth
                  getOptionLabel={(option) => option.name}
                  options={evaluationTypes}
                  disable={locked || type === "update"}
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                <FormInput
                  size="small"
                  name="integrationId"
                  fullWidth
                  disabled={locked}
                  label={
                    t(translationPath + "integration_id") || "Integration Id"
                  }
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                <FormInput
                  size="small"
                  name="title"
                  fullWidth
                  disabled={locked}
                  label={t(translationPath + "title") || "Title"}
                />
              </Grid>

              <QuestionDescription />

              <QuestionCategory />

              <QuestionAlternatives />

              <QuestionOptionType />

              <QuestionGuidanceTextPosition />

              <QuestionGuidanceText disabled={locked} />
            </Grid>
          </Grid>
        </Grid>
      )}
    />
  );
};

export default Question;
